<!--
  Sales (대시보드) — Pineapple 톤
  ────────────────────────────────────
  · 기존 비즈니스 로직 / API 호출 / 데이터 셰이프 모두 보존.
  · 표현 레이어만 Material → Pineapple 로 교체:
    - DefaultStatisticsCard      → AStat + ACard
    - 차트 카드들                 → ACard 로 래핑 (내부 Chart.js 컴포넌트는 그대로)
    - 최근 문의/시험 리스트       → ACard + ATable
    - 최근 활동 테이블 컴포넌트   → ACard 래퍼 + 자체 컴포넌트 유지
-->
<template>
  <div class="p-page">
    <a-page-head title="대시보드" :breadcrumbs="[{ label: '관리자' }, { label: '대시보드' }]">
      <template #actions>
        <a-btn kind="ghost" size="md" @click="loadDashboardData">
          <template #icon>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="1 4 1 10 7 10" />
              <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
            </svg>
          </template>
          새로고침
        </a-btn>
      </template>
    </a-page-head>

    <!-- 로딩 -->
    <div v-if="loading" class="p-page__loader">
      <div class="p-spinner" />
      <p>데이터를 불러오는 중입니다...</p>
    </div>

    <template v-else>
      <!-- KPI 카드들 -->
      <div class="p-grid p-grid--kpi">
        <a-card padding="20">
          <a-stat
            label="전체 클래스"
            :value="String(statistics.totalClasses || 0)"
            :delta="`+${statistics.thisMonthNewClasses || 0}`"
            delta-kind="ok"
            delta-suffix="이번 달 신규"
          >
            <div class="p-kpi-meta p-mono">{{ currentPeriod.start }} ~ {{ currentPeriod.end }}</div>
            <div class="p-kpi-tags">
              <button
                v-for="opt in periodOptions"
                :key="opt.key"
                class="p-kpi-tag"
                :class="{ 'is-active': activePeriod === opt.key }"
                @click="setPeriod(opt.key)"
              >{{ opt.label }}</button>
            </div>
          </a-stat>
        </a-card>
        <a-card padding="20">
          <a-stat
            label="진행중 시험"
            :value="String(statistics.activeExams || 0)"
          >
            <div class="p-kpi-line">
              <span class="p-mono">{{ statistics.totalActiveStudents || 0 }}명</span>
              <span class="p-kpi-line__sub">참여중</span>
            </div>
            <div class="p-kpi-line">
              <span class="p-status-dot is-live" />
              <span class="p-kpi-line__sub">실시간 모니터링</span>
            </div>
          </a-stat>
        </a-card>
        <a-card padding="20">
          <a-stat
            label="문제은행"
            :value="String(statistics.totalQuestions || 0)"
          >
            <div class="p-kpi-line">
              <span class="p-mono">{{ statistics.verifiedQuestions || 0 }}개</span>
              <span class="p-kpi-line__sub">검증완료</span>
            </div>
          </a-stat>
        </a-card>
      </div>

      <!-- 차트 영역 -->
      <div class="p-grid p-grid--charts">
        <a-card title="클래스 상태" sub="진행중 / 예정 / 완료 / 중단" padding="16">
          <class-status-chart-card v-if="hasClassData" :chart-data="classStatusData" :bare="true" />
          <a-empty v-else title="클래스 데이터가 없습니다" sub="클래스를 등록하면 상태 분포가 표시됩니다." />
        </a-card>
        <a-card title="시험 트렌드" sub="최근 6개월 시험 실시 추이" padding="16">
          <exam-trend-chart-card v-if="hasExamData" :chart-data="examTrendData" :bare="true" />
          <a-empty v-else title="시험 데이터가 없습니다" sub="시험을 생성하면 트렌드 그래프가 표시됩니다." />
        </a-card>
      </div>

      <!-- 최근 문의 & 최근 시험 -->
      <div class="p-grid p-grid--lists">
        <a-card title="최근 문의 현황" sub="가장 최근 5건">
          <template #action>
            <a-btn size="sm" kind="ghost" @click="loadRecentInquiries">
              <template #icon>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="1 4 1 10 7 10" />
                  <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                </svg>
              </template>
              새로고침
            </a-btn>
          </template>

          <a-table
            v-if="recentInquiries.length"
            :columns="inquiryCols"
            :rows="recentInquiries"
            dense
          >
            <template #cell-status="{ row }">
              <a-pill :kind="inquiryStatusKind(row.status)">{{ row.status }}</a-pill>
            </template>
            <template #cell-category="{ row }">
              {{ getCategoryLabel(row.category) }}
            </template>
            <template #cell-subject="{ row }">
              <span class="p-clip">{{ row.subject }}</span>
            </template>
            <template #cell-created_at="{ row }">
              <span class="p-mono">{{ formatShortDate(row.created_at) }}</span>
            </template>
          </a-table>
          <a-empty v-else title="최근 문의가 없습니다" />
        </a-card>

        <a-card title="최근 시험 현황" sub="내가 생성한 시험 5건">
          <a-table
            v-if="recentExams.length"
            :columns="examCols"
            :rows="recentExams"
            dense
          >
            <template #cell-status="{ row }">
              <a-pill :kind="examStatusKind(row.status)">{{ examStatusLabel(row.status) }}</a-pill>
            </template>
            <template #cell-participants="{ row }">
              <span class="p-mono">{{ row.participants }}명</span>
            </template>
            <template #cell-avgScore="{ row }">
              <span class="p-mono">{{ row.avgScore }}</span>
            </template>
          </a-table>
          <a-empty v-else title="최근 시험이 없습니다" />
        </a-card>
      </div>

      <!-- 최근 활동 -->
      <a-card title="최근 학습 활동" sub="시간 순 정렬">
        <recent-activity-table
          v-if="recentActivities.length"
          :activities="recentActivities"
          :bare="true"
          @refresh="loadRecentActivities"
        />
        <a-empty v-else title="최근 활동 내역이 없습니다" sub="학생들이 학습을 시작하면 여기 표시됩니다." />
      </a-card>
    </template>
  </div>
</template>

<script>
import ClassStatusChartCard from "./components/ClassStatusChartCard.vue";
import ExamTrendChartCard from "./components/ExamTrendChartCard.vue";
import RecentActivityTable from "./components/RecentActivityTable.vue";
import APageHead from "@/components/Pineapple/APageHead.vue";
import ACard from "@/components/Pineapple/ACard.vue";
import AStat from "@/components/Pineapple/AStat.vue";
import ATable from "@/components/Pineapple/ATable.vue";
import APill from "@/components/Pineapple/APill.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import AEmpty from "@/components/Pineapple/AEmpty.vue";
import { parseUtc } from "@/utils/datetime";
import examService from "@/services/examService";

export default {
  name: "AcademicDashboard",
  components: {
    ClassStatusChartCard,
    ExamTrendChartCard,
    RecentActivityTable,
    APageHead, ACard, AStat, ATable, APill, ABtn, AEmpty,
  },
  data() {
    return {
      loading: true,
      statistics: {
        totalClasses: 0,
        thisMonthNewClasses: 0,
        activeExams: 0,
        totalActiveStudents: 0,
        totalQuestions: 0,
        verifiedQuestions: 0,
      },
      currentPeriod: {
        start: new Date().toISOString().split('T')[0],
        end: new Date().toISOString().split('T')[0],
      },
      activePeriod: 'today',
      periodOptions: [
        { key: '7d',    label: '최근 7일' },
        { key: '30d',   label: '최근 30일' },
        { key: 'month', label: '이번 달' },
      ],
      classStatusData: {
        labels: ['진행중', '예정', '완료', '중단'],
        datasets: { label: '클래스 상태', data: [0, 0, 0, 0] },
      },
      examTrendData: {
        labels: [],
        datasets: [
          { label: '시험 실시', data: [] },
          { label: '평균 점수', data: [] },
        ],
      },
      recentInquiries: [],
      recentExams: [],
      recentActivities: [],
      inquiryCols: [
        { key: 'status',      label: '상태',    width: '90px' },
        { key: 'category',    label: '카테고리', width: '110px' },
        { key: 'subject',     label: '제목',    wrap: true },
        { key: 'user_name',   label: '작성자',  width: '110px' },
        { key: 'created_at',  label: '작성',    width: '90px', align: 'right' },
      ],
      examCols: [
        { key: 'name',          label: '시험명',  wrap: true },
        { key: 'status',        label: '상태',    width: '90px' },
        { key: 'participants',  label: '참여',    width: '70px', align: 'right' },
        { key: 'avgScore',      label: '평균',    width: '60px', align: 'right' },
      ],
    };
  },

  computed: {
    hasClassData() {
      return this.classStatusData.datasets.data.some(v => v > 0);
    },
    hasExamData() {
      return this.examTrendData.labels.length > 0
        && this.examTrendData.datasets.some(ds => ds.data.length > 0);
    },
  },

  mounted() {
    this.loadDashboardData();
  },

  methods: {
    /** 기간 선택 버튼 — currentPeriod 범위를 계산해 그리고 데이터 재로드 */
    setPeriod(key) {
      this.activePeriod = key;
      const end = new Date();
      const start = new Date();
      if (key === '7d')        start.setDate(end.getDate() - 6);
      else if (key === '30d')  start.setDate(end.getDate() - 29);
      else if (key === 'month') start.setDate(1);
      this.currentPeriod = {
        start: start.toISOString().split('T')[0],
        end:   end.toISOString().split('T')[0],
      };
      // 데이터 재로드 (필요 시 API에 기간 파라미터 전달하도록 추후 확장)
      this.loadDashboardData();
    },
    inquiryStatusKind(status) {
      if (status === '답변완료') return 'ok';
      if (status === '처리중')   return 'info';
      return 'warn';
    },
    examStatusKind(status) {
      if (status === 'completed') return 'ok';
      if (status === 'active' || status === 'in_progress') return 'info';
      if (status === 'scheduled') return 'warn';
      return 'default';
    },
    examStatusLabel(status) {
      const map = {
        completed: '완료', active: '진행중', in_progress: '진행중',
        scheduled: '예정', draft: '초안',
      };
      return map[status] || status;
    },

    getCurrentUserEmail() {
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      const token = localStorage.getItem('access_token');
      let tokenUser = null;
      if (token) {
        try {
          const payload = token.split('.')[1];
          tokenUser = JSON.parse(atob(payload));
        } catch (e) { console.warn('토큰 파싱 실패:', e); }
      }
      return tokenUser?.sub || tokenUser?.email || userInfo?.email || userInfo?.userEmail || null;
    },

    // 본인 시험 전체 — GET /exams/ 는 기본 10건·최대 100건씩이라 모든 페이지를 받는다 (docs/시험_관리_API.md 3절)
    async fetchMyExams() {
      const email = this.getCurrentUserEmail();
      if (!email) return [];
      const exams = await examService.fetchAllExams({ creator_email: email });
      return exams.filter(e => e.creatorEmail === email);
    },

    // 담당 클래스 전체 — 강사 토큰은 서버가 본인 클래스로 거른다 (docs/학습_API_명세.md 1.2)
    async fetchMyClasses() {
      const r = await this.$axios({
        method: 'get',
        url: '/learning/allClasses',
        params: { limit: 1000, page: 1 },
        loadingBar: false,
      });
      return r.data || null;
    },

    // 시험 목록·클래스 목록은 한 번씩만 받아 통계·차트·최근 목록이 나눠 쓴다
    // (전에는 통계·추이·최근 시험이 각자 시험 전 페이지를 받아 3회, 통계·상태 차트가 클래스 목록을 2회 받았다 — 2026-10-08)
    async loadDashboardData() {
      this.loading = true;
      try {
        const [classes, myExams] = await Promise.all([
          this.fetchMyClasses().catch(e => { console.warn('클래스 통계 로드 실패:', e.message); return null; }),
          this.fetchMyExams().catch(e => { console.warn('시험 통계 로드 실패:', e.message); return null; }),
          this.loadQuestionCount(),
          this.loadRecentInquiries(),
          this.loadRecentActivities(),
        ]);
        if (classes) this.applyClassData(classes);
        if (myExams) this.applyExamData(myExams);
      } catch (e) {
        console.error('대시보드 데이터 로드 실패:', e);
      } finally {
        this.loading = false;
      }
    },

    // 클래스 수·이달 신규·상태 분포
    applyClassData(data) {
      this.statistics.totalClasses = data.total_count || 0;
      const items = data.items || [];
      // created_at 은 'YYYY-MM-DD'(KST 달력일, 시각 없음) — Date 로 바꾸면 브라우저 시간대에 따라 하루 밀리므로 앞 7자로 비교 (2026-10-08 플랫폼 백엔드 회신)
      const now = new Date();
      const thisMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      this.statistics.thisMonthNewClasses = items.filter(item => String(item.created_at || '').slice(0, 7) === thisMonthKey).length;
      const c = { ongoing: 0, scheduled: 0, completed: 0, suspended: 0 };
      items.forEach(item => {
        const s = item.class_status;
        if (['ongoing', 'in_progress', 'active'].includes(s)) c.ongoing++;
        else if (['scheduled', 'pending'].includes(s)) c.scheduled++;
        else if (['completed', 'finished'].includes(s)) c.completed++;
        else if (['suspended', 'paused', 'inactive'].includes(s)) c.suspended++;
      });
      this.classStatusData.datasets.data = [c.ongoing, c.scheduled, c.completed, c.suspended];
    },

    // 진행 중 시험 수·최근 6개월 추이·최근 시험 5건
    applyExamData(myExams) {
      this.statistics.activeExams = myExams.filter(e => e.status === 'active' || e.status === 'in_progress').length;
      this.statistics.totalActiveStudents = 0;

      const now = new Date();
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(now.getMonth() - 6);
      const monthlyData = {};
      for (let i = 5; i >= 0; i--) {
        const date = new Date();
        date.setMonth(now.getMonth() - i);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        monthlyData[monthKey] = { label: `${date.getMonth() + 1}월`, count: 0, scores: [] };
      }
      myExams.forEach(exam => {
        if (exam.startTimeUTC) {
          const d = parseUtc(exam.startTimeUTC);
          if (d && d >= sixMonthsAgo) {
            const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            if (monthlyData[monthKey]) monthlyData[monthKey].count++;
          }
        }
      });
      const labels = Object.values(monthlyData).map(d => d.label);
      const examCounts = Object.values(monthlyData).map(d => d.count);
      if (examCounts.some(c => c > 0)) {
        this.examTrendData.labels = labels;
        this.examTrendData.datasets[0].data = examCounts;
        this.examTrendData.datasets[1].data = [];
      }

      this.recentExams = myExams
        .filter(e => e.startTimeUTC)
        .sort((a, b) => (parseUtc(b.startTimeUTC)?.getTime() || 0) - (parseUtc(a.startTimeUTC)?.getTime() || 0))
        .slice(0, 5)
        .map(e => ({
          name: e.examName || '이름 없음',
          status: e.status || 'draft',
          participants: 0,
          avgScore: '-',
        }));
    },

    async loadQuestionCount() {
      try {
        const r = await this.$axios({
          method: 'get',
          url: '/questions/my-questions/count',
          api_url: this.$CONF.ACHIEVE_API_URL,
          loadingBar: false,
        });
        if (r.data) {
          this.statistics.totalQuestions = r.data.total_count || 0;
          this.statistics.verifiedQuestions = r.data.verified_count || 0;
        }
      } catch (e) { console.warn('문제은행 통계 로드 실패:', e.message); }
    },

    // 최신 5건만 받는다 (docs/헬프데스크_백엔드_API_명세.md 2.2.1 의 sort·limit). 정렬은 서버가 무시해도 되도록 화면에서 한 번 더 한다
    async loadRecentInquiries() {
      try {
        const r = await this.$axios({
          method: 'get',
          url: '/helpdesk/inquiries',
          params: { sort: 'latest', limit: 5 },
          loadingBar: false,
        });
        if (r.data && r.data.inquiries) {
          this.recentInquiries = r.data.inquiries
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
            .slice(0, 5);
        }
      } catch (e) { console.warn('최근 문의 데이터 로드 실패:', e.message); }
    },

    async loadRecentActivities() {
      this.recentActivities = [];
    },

    getCategoryLabel(category) {
      const map = {
        general: '일반문의', technical: '기술지원', payment: '결제/환불',
        account: '계정문의', content: '콘텐츠문의', other: '기타',
      };
      return map[category] || category;
    },

    formatShortDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      const now = new Date();
      const diff = Math.floor(Math.abs(now - date) / (1000 * 60 * 60 * 24));
      if (diff === 0) return '오늘';
      if (diff === 1) return '어제';
      if (diff < 7) return `${diff}일 전`;
      return date.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/_pineapple.scss';

.p-page {
  font-family: $p-font-body;
  color: $p-ink;

  &__loader {
    padding: 80px 0;
    text-align: center;
    color: $p-ink-mid;
    font-size: 13px;
  }
}

.p-spinner {
  display: inline-block;
  width: 36px;
  height: 36px;
  border: 3px solid $p-line;
  border-top-color: $p-point;
  border-radius: 50%;
  animation: p-spin 0.8s linear infinite;
}
@keyframes p-spin {
  to { transform: rotate(360deg); }
}

.p-grid {
  display: grid;
  gap: 16px;
  margin-bottom: 16px;

  &--kpi    { grid-template-columns: repeat(3, 1fr); }
  &--charts { grid-template-columns: 1fr 2fr; }
  &--lists  { grid-template-columns: 2fr 1fr; }

  @media (max-width: 1100px) {
    &--kpi, &--charts, &--lists { grid-template-columns: 1fr; }
  }
}

.p-clip {
  display: inline-block;
  max-width: 380px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}
.p-mono { font-family: $p-font-mono; font-size: 11px; }

/* KPI 카드 안 보조 정보 */
.p-kpi-meta {
  color: $p-ink2;
  font-size: 11px;
}
.p-kpi-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
}
.p-kpi-tag {
  font-family: $p-font-body;
  font-size: 10px;
  font-weight: 700;
  padding: 3px 8px;
  background: $p-bg;
  border: 1px solid $p-line;
  border-radius: 999px;
  color: $p-ink-mid;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover { background: $p-line-soft; color: $p-ink; }
  &.is-active {
    background: $p-ink;
    color: $p-point;
    border-color: $p-ink;
  }
}
.p-kpi-line {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;

  &__sub { color: $p-ink-soft; }
}
.p-status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: $p-ink-softer;
  flex-shrink: 0;

  &.is-live {
    background: $p-ok;
    box-shadow: 0 0 0 3px rgba(30, 140, 102, 0.18);
    animation: p-pulse 2s ease-in-out infinite;
  }
}
@keyframes p-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}
</style>
